<script setup lang="ts">
import { api } from '@/api';
import { ReferredEntityChange, TaskInJobDto } from '@/api/generator';
import { useFormMode } from '@/components/forms/useFormMode';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import Notification from '@/components/notifications/Notification.vue';
import { NotificationMessage } from '@/components/notifications/types';
import StateOfUseBadge from '@/components/stateOfUse/StateOfUseBadge.vue';
import { I18nKey } from '@/utils/typings';
import FormAddButton from '@/views/tasks/components/FormAddButton.vue';
import { mdiChevronDown, mdiChevronUp, mdiDragHorizontal, mdiPlus } from '@mdi/js';
import { useToggle } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';
import { Sortable } from 'sortablejs-vue3';
import { useFieldArray, useValidateField } from 'vee-validate';
import { computed, ref, toRef, watch, watchEffect } from 'vue';
import { useDraggableExpandpanels } from '../utils';
import JobConfigurationTaskDetails from './JobConfigurationTaskDetails.vue';
import JobConfigurationTaskSelectionDialog from './JobConfigurationTaskSelectionDialog.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import { routenames } from '@/router/routenames';
import InternalLink from '@/components/InternalLink/InternalLink.vue';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import { getValidationErrorType } from '../utils/jobConfigurationTaskHelpers';

const { t } = useTranslation();

const props = defineProps<{
  name: string;
  targetSystemEntityId?: string;
}>();

const mode = useFormMode();

const { fields, remove, move, replace } = useFieldArray<TaskInJobDto>(toRef(() => props.name));

const validate = useValidateField('targetSystem');

const [isDialogOpen, toggle] = useToggle(false);

async function toggleDialog(open: boolean) {
  toggle(open);
}

function toggleActivation(index: number) {
  const task = fields.value[index].value;
  task.disabled = !task.disabled;
}

const selected = computed(() => fields.value.map((field) => field.value));

async function updateSelection(tasks: TaskInJobDto[]) {
  const hadChanges = tasks.length !== fields.value.length;

  replace(tasks);

  // WORKAROUND HACK: vee-validate's replace() updates both current AND initial values simultaneously,
  // so the form never becomes dirty. We force dirty state by temporarily toggling
  // a field state, which creates a change that vee-validate detects.
  if (hadChanges && tasks.length > 0) {
    const firstTask = fields.value[0].value;
    const originalDisabledState = firstTask.disabled;
    firstTask.disabled = !originalDisabledState;
    // the timeout is needed to ensure that the change is detected by vee-validate
    setTimeout(() => {
      firstTask.disabled = originalDisabledState;
    }, 0);
  }

  await validate();
}

const {
  sortableOptions,
  drag,
  dragStart,
  expandedIndices,
  expandAll,
  collapseAll,
  anyExpanded,
  allExpanded,
  isDragging,
} = useDraggableExpandpanels(move, mode, fields);

async function updateTaskSelection(targetSystemId: string | undefined) {
  if (targetSystemId) {
    const { result: newTaskOptions } = await api.jobConfigurationsApi.getUpdatedTaskItems({
      getUpdatedTaskItemsDto: {
        targetSystemId,
        tasks: fields.value.map((field) => field.value),
      },
    });

    function isAlreadyAssigned(option: TaskInJobDto) {
      return fields.value.some((field) => field.value.taskId === option.taskId);
    }

    const newTasks = (newTaskOptions ?? []).filter(isAlreadyAssigned);

    updateSelection(newTasks);
  }
}

const tasksChangedNotification = ref<NotificationMessage | undefined>(undefined);

watchEffect(() => {
  const someTaskChanged = fields.value.some(({ value: task }) => task.taskChange !== 'None');

  if (someTaskChanged) {
    tasksChangedNotification.value = {
      type: 'warning',
      message: t('SomeTaskModifiedMsg'),
    };
  }
});

function getTaskNotification(task: TaskInJobDto): NotificationMessage | undefined {
  return task.taskChange === ReferredEntityChange.Updated
    ? {
        type: 'warning',
        closable: false,
        message: t('TaskModifiedMsg'),
      }
    : undefined;
}

function toggleNotification(show: boolean = true) {
  const notification: NotificationMessage = {
    type: 'warning',
    message: t('TargetSystemChangedMsg'),
  };

  tasksChangedNotification.value = show ? notification : undefined;
}

watch(
  () => props.targetSystemEntityId,
  (targetSystemId) => {
    if (targetSystemId) {
      updateTaskSelection(targetSystemId);
      toggleNotification(!!fields.value.length);
    } else {
      collapseAll();
    }
  },
);
</script>

<template>
  <v-card class="container-level-1">
    <v-card-item>
      <v-card-title>
        <h5 class="mb-0">{{ $t('WorkList') }}</h5>
        <VSpacer />

        <FormAddButton
          v-if="mode !== 'details'"
          :prependIcon="mdiPlus"
          :disabled="!targetSystemEntityId"
          :label="'SelectWorkItem_other'"
          @click="toggleDialog(true)"
        />
      </v-card-title>
    </v-card-item>

    <v-card-text>
      <Notification
        class="mt-4 mb-4"
        :value="tasksChangedNotification"
        @click:close="toggleNotification(false)"
      />

      <div v-if="fields.length > 1" class="d-flex w-100 justify-end">
        <TextVBtn
          :text="$t('ExpandAll')"
          :prependIcon="mdiChevronDown"
          size="x-small"
          :disabled="allExpanded"
          class="text-caption"
          @click="expandAll"
        />

        <TextVBtn
          :text="$t('CollapseAll')"
          :prependIcon="mdiChevronUp"
          size="x-small"
          :disabled="!anyExpanded"
          class="text-caption"
          @click="collapseAll"
        />
      </div>
      <p v-if="fields.length === 0" class="text-body-1 text-primary-lighten-2 font-weight-semibold">
        {{ $t('AddTasksToJobMsg') }}
      </p>

      <v-expansion-panels
        v-model="expandedIndices"
        class="mt-2"
        :disabled="!targetSystemEntityId"
        :readonly="isDragging"
        multiple
        @update:modelValue="toggleNotification(false)"
      >
        <Sortable
          :list="fields"
          :options="sortableOptions"
          class="w-100"
          itemKey="taskId"
          @start="dragStart"
          @end="drag"
          @change="toggleNotification(false)"
        >
          <template #item="{ element: field, index }">
            <v-expansion-panel
              :key="field.value.taskId || undefined"
              :title="field.value.name || undefined"
              :text="field.value.description || undefined"
              class="tasks"
            >
              <template #title>
                <div class="title-container d-flex w-100 align-center">
                  <div class="task-name-container me-auto d-flex align-center">
                    <v-icon
                      v-if="!sortableOptions.disabled"
                      :icon="mdiDragHorizontal"
                      class="mr-2 handle cursor-move"
                    />

                    <v-checkbox
                      :disabled="mode === 'details'"
                      class="mr-2"
                      hideDetails
                      :modelValue="!field.value.disabled"
                      @click.stop="toggleActivation(index)"
                    />

                    <IdentifierLabel
                      :label="field.value.name"
                      :identifier="field.value.iuid"
                      :subline="$t(`TaskTypeDescriptionEnum.${field.value.taskType}` as I18nKey)"
                      class="text-h5"
                    >
                      <template #suffix>
                        <InternalLink
                          v-if="field.value.taskId"
                          :permissions="['Global_TaskManagement_ViewDetail']"
                          :tooltip="$t('GetToTaskItem')"
                          :to="{
                            name: routenames.tasks.details,
                            params: {
                              id: field.value.taskId,
                            },
                          }"
                        />
                      </template>
                    </IdentifierLabel>
                  </div>

                  <div class="notification-container mr-2">
                    <Notification :value="getTaskNotification(field.value)" />

                    <InlineValidationError
                      validateOnMount
                      :name="`tasks[${index}]`"
                      :type="getValidationErrorType(field.value)"
                      ignoreTouched
                    />
                  </div>

                  <StateOfUseBadge class="mr-4" :value="field.value.state" />

                  <PtwDeleteButton :hidden="mode === 'details'" @delete="remove(index)" />
                </div>
              </template>

              <template #text>
                <JobConfigurationTaskDetails
                  v-if="field.value.taskId && targetSystemEntityId"
                  :name="`${name}[${index}]`"
                  :targetSystemEntityId="targetSystemEntityId"
                />
              </template>
            </v-expansion-panel>
          </template>
        </Sortable>
      </v-expansion-panels>
    </v-card-text>
  </v-card>

  <JobConfigurationTaskSelectionDialog
    v-if="targetSystemEntityId"
    v-model:open="isDialogOpen"
    :targetSystemId="targetSystemEntityId"
    :selected="selected"
    @update:selected="updateSelection"
  />
</template>

<style scoped lang="scss">
:deep(.tasks) {
  .v-expansion-panel-title {
    padding: 0px 16px 0px 8px;

    .title-container {
      .task-name-container {
        min-width: 0;
        flex: 1;
      }

      .notification-container {
        flex-shrink: 0;

        .v-alert {
          white-space: nowrap;
          min-width: max-content;
        }
      }
    }
  }
}
</style>
